// Optimized API Service for 100+ Users
import { API_CONFIG, getApiUrl } from "../config/api";

class ApiService {
  constructor() {
    this.cache = new Map();
    this.requestQueue = new Map();
    this.activeRequests = new Set();
  }

  // Request caching
  getCacheKey(url, options = {}) {
    const method = options.method || "GET";
    const body = options.body ? JSON.stringify(options.body) : "";
    return `${method}:${url}:${body}`;
  }

  getFromCache(key) {
    if (!API_CONFIG.CACHE_ENABLED) return null;

    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < API_CONFIG.CACHE_DURATION) {
      return cached.data;
    }

    this.cache.delete(key);
    return null;
  }

  setCache(key, data) {
    if (!API_CONFIG.CACHE_ENABLED) return;

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
    });
  }

  // Request deduplication
  async deduplicateRequest(url, options) {
    const key = this.getCacheKey(url, options);

    if (this.activeRequests.has(key)) {
      // Wait for existing request to complete
      return new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          if (!this.activeRequests.has(key)) {
            clearInterval(checkInterval);
            const cached = this.getFromCache(key);
            if (cached) {
              resolve(cached);
            } else {
              reject(new Error("Request failed"));
            }
          }
        }, 100);
      });
    }

    this.activeRequests.add(key);
    return key;
  }

  // Retry logic with exponential backoff
  async retryRequest(url, options, retryCount = 0) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);

      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...options.headers,
        },
      });

      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      if (retryCount < API_CONFIG.MAX_RETRIES && this.shouldRetry(error)) {
        const delay = API_CONFIG.RETRY_DELAY * Math.pow(2, retryCount);
        await new Promise((resolve) => setTimeout(resolve, delay));
        return this.retryRequest(url, options, retryCount + 1);
      }
      throw error;
    }
  }

  shouldRetry(error) {
    // Retry on network errors, timeouts, and 5xx errors
    return (
      error.name === "AbortError" ||
      error.name === "TypeError" ||
      (error.status >= 500 && error.status < 600)
    );
  }

  // Optimized fetch method
  async fetch(endpoint, options = {}) {
    const url = getApiUrl(endpoint);
    const method = options.method || "GET";
    const cacheKey = this.getCacheKey(url, options);

    // Check cache for GET requests
    if (method === "GET") {
      const cached = this.getFromCache(cacheKey);
      if (cached) {
        console.log(`Cache hit for ${endpoint}`);
        return cached;
      }
    }

    // Deduplicate requests
    const requestKey = await this.deduplicateRequest(url, options);

    try {
      const response = await this.retryRequest(url, options);

      // Handle response
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const textResponse = await response.text();
        throw new Error(`Non-JSON response: ${textResponse.substring(0, 100)}`);
      }

      const data = await response.json();

      // Cache successful GET requests
      if (method === "GET" && data.success !== false) {
        this.setCache(cacheKey, data);
      }

      return data;
    } catch (error) {
      console.error(`API Error for ${endpoint}:`, error);
      throw error;
    } finally {
      this.activeRequests.delete(requestKey);
    }
  }

  // Optimized login method
  async login(email, password) {
    try {
      const data = await this.fetch(API_CONFIG.ENDPOINTS.LOGIN, {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });

      if (data.success) {
        // Clear cache on successful login to get fresh user data
        this.clearUserCache();
      }

      return data;
    } catch (error) {
      return {
        success: false,
        error: error.message || "Login failed. Please check your connection.",
      };
    }
  }

  // Optimized logout method
  async logout(token) {
    try {
      await this.fetch(API_CONFIG.ENDPOINTS.LOGOUT, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (error) {
      console.error("Logout API error:", error);
    } finally {
      // Clear all cache on logout
      this.clearAllCache();
    }
  }

  // Verify session with caching
  async verifySession(token) {
    if (!token) return { success: false };

    try {
      const data = await this.fetch(API_CONFIG.ENDPOINTS.VERIFY, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return data;
    } catch (error) {
      console.error("Session verification error:", error);
      return { success: false };
    }
  }

  // Performance monitoring methods
  async getPerformanceMetrics(token) {
    return this.fetch(API_CONFIG.ENDPOINTS.PERFORMANCE_METRICS, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getDatabaseHealth(token) {
    return this.fetch(API_CONFIG.ENDPOINTS.PERFORMANCE_DB_HEALTH, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async optimizeDatabase(token) {
    return this.fetch(API_CONFIG.ENDPOINTS.PERFORMANCE_OPTIMIZE_DB, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async clearCache(token) {
    const result = await this.fetch(API_CONFIG.ENDPOINTS.PERFORMANCE_CLEAR_CACHE, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (result.success) {
      this.clearAllCache();
    }

    return result;
  }

  // Cache management
  clearUserCache() {
    const keysToDelete = [];
    for (const key of this.cache.keys()) {
      if (
        key.includes("/api/tasks") ||
        key.includes("/api/ledgers") ||
        key.includes("/api/orders")
      ) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach((key) => this.cache.delete(key));
  }

  clearAllCache() {
    this.cache.clear();
  }

  // Get cache statistics
  getCacheStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
      activeRequests: this.activeRequests.size,
    };
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;
